'use client';

import { useEffect, useRef, useState } from 'react';
import { usePipelineStore } from '@/lib/pipeline/store';
import { EXECUTORS, PREVIEW_CONTEXT } from '@/lib/pipeline/executors';
import { NODE_SPECS } from '@/lib/pipeline/registry';
import { resolveNodeInputs } from '@/lib/pipeline/engine';

const DEBOUNCE_MS = 120;

/** Value-identity equality for resolved-inputs maps. */
const sameInputs = (a: Record<string, unknown>, b: Record<string, unknown>) => {
  const ak = Object.keys(a);
  return ak.length === Object.keys(b).length && ak.every((k) => a[k] === b[k]);
};

/**
 * Live preview for cheap client-only nodes (NodeSpec.livePreview): debounced re-run of the node's
 * executor as its params or upstream outputs change, shown node-locally — no Run, not fed
 * downstream. Input-dependent nodes preview once their upstream has run ("run once, then live");
 * the executor throwing on a missing input gates that case. Also returns the node's resolved
 * upstream inputs (e.g. for a placement-pad backdrop).
 */
export function useLivePreview(id: string, nodeType: string, data: Record<string, unknown>) {
  const edges = usePipelineStore((s) => s.edges);
  const runtime = usePipelineStore((s) => s.runtime);
  const [preview, setPreview] = useState<string | null>(null);

  // Keep the inputs map identity-stable across renders so the effect only re-fires when an
  // actual input value changes, not on every unrelated runtime/edges update.
  const inputs = resolveNodeInputs(id, edges, (src) => runtime[src]?.output);
  const inputsRef = useRef(inputs);
  if (!sameInputs(inputsRef.current, inputs)) inputsRef.current = inputs;
  const stableInputs = inputsRef.current;

  useEffect(() => {
    const fn = NODE_SPECS[nodeType]?.livePreview ? EXECUTORS[nodeType] : undefined;
    if (!fn) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fn(stableInputs, data, PREVIEW_CONTEXT);
        if (!cancelled && typeof res?.image === 'string') setPreview(res.image);
      } catch {
        // Missing required input (upstream not run yet) etc. — clear the preview, non-fatal.
        if (!cancelled) setPreview(null);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [nodeType, data, stableInputs]);

  return { preview, inputs: stableInputs };
}
